import { useEffect, useState, useRef } from 'react';
import { usePostMessage, DeviceType, useSubscribeData } from 'spectacle-ui-game-bridge-react';
import useDlogLoginFunnel, { LoginFunnelTargets } from './useDlogLoginFunnel';
import { PreloginContent } from './useContentForBuild';
import { usePreloginContext } from '../context/PreloginContext';
import { ErrorMessage } from '../components/errorModal/errorModal';
import lerp from '../utils/lerp';
import clamp from '../utils/clamp';

type UseCDNIResult = {
  autoLoginStatus: number | null;
  buffer: number;
  closeWebView: () => void;
  diskFreeSize: number;
  error: ErrorMessage | null;
  fireExit: () => void;
  GetAverageDownloadSpeed: number;
  GetDownloadedSizeInBytes: number;
  getMb: (bytes: number) => string;
  GetTotalSizeToDownload: number;
  InstallingVersion: number;
  isCDNIFinished: boolean;
  isCheckingForUpdates: boolean;
  isLoading: boolean;
  isMobileSharedAccountEnabled: boolean;
  NowVersion: number;
  openDeviceStorage: () => void;
  ShowUsingDevTip: boolean;
  CDNICellularDownloadTipClose: boolean | null;
  CDNIUnexpectedError: boolean | null;
  CDNIPromptName: string | null;
  CDNIRecoverableErrorMessage: string | null;
  CDNIGoToAppStore: boolean | null;
};

export default function useCDNI(content: PreloginContent | null): UseCDNIResult {
  const [error, setError] = useState<ErrorMessage | null>(null);
  const [buffer, setBuffer] = useState<number>(0);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const prevTick = useRef<number>(0);
  const dlog = useDlogLoginFunnel();
  const isConnected = useSubscribeData<boolean>('webviewdata', 'isConnected');
  const { autoLoginStatus, CDNICellularDownloadTip, CDNIGoToAppStore, CDNICellularDownloadTipClose, CDNIPromptErrorMessage, CDNIPromptMessage, CDNIPromptName, CDNIRecoverableErrorMessage, CDNIRestart, CDNIShowDiskSpaceFailedPopup, CDNIUnexpectedError, CDNIUnexpectedErrorCode, CDNIUnexpectedErrorMessage, device_os, diskFreeSize, GetAverageDownloadSpeed, GetDownloadedSizeInBytes, GetTotalSizeToDownload, InstallingVersion, isCDNIFinished, isMobileSharedAccountEnabled, isUsingCachedLoginToken, NowVersion, ShowUsingDevTip, CDNIPromptUseDevServer } = usePreloginContext();

  // Posts
  const CDNI_GoToAppStore = usePostMessage('CDNI_GoToAppStore');
  const CDNI_Quit = usePostMessage('CDNI_Quit');
  const CDNI_RestoreState = usePostMessage('CDNI_RestoreState');
  const CDNI_SystemRestart = usePostMessage('CDNI_SystemRestart');
  const CDNI_SetAllowCellularDownload = usePostMessage('CDNI_SetAllowCellularDownload');
  const CDNI_IgnoreCheckDiskSpaceFailure = usePostMessage('CDNI_IgnoreCheckDiskSpaceFailure');
  const CDNI_UseDevServer = usePostMessage('CDNI_UseDevServer');
  const closeWebView = usePostMessage('closeWebView');
  const openDeviceStorage = usePostMessage('openDeviceStorage');

  const getMb = (bytes: number) => {
    return Number(bytes / 1024 ** 2).toFixed(2);
  };

  const fireExit = () => {
    if (error?.cdniExit) {
      error.cdniExit();
    }

    dlog({
      phase: 'CDNI_Screen_Error',
      action: 'Click',
      targets: (error?.cta ?? 'Retry') as LoginFunnelTargets,
    });

    if (!error?.persistOnExit) {
      setError(null);
    }
  };

  // CDNIPromptUseDevServer
  useEffect(() => {
    if (content) {
      if (CDNIPromptUseDevServer) {
        setError({
          heading: 'CDNI ENV Selection',
          message: 'Would you like to use the development CDNI server?',
          cta: 'Use Dev Server',
          cdniExit: CDNI_UseDevServer,
        });
      }
    }
  }, [content, CDNIPromptUseDevServer, CDNI_UseDevServer]);

  // CDNIRebootPopup
  useEffect(() => {
    if (content) {
      if (CDNIRestart) {
        setError({
          heading: content.notice,
          message: content.restartRequest,
        });
      }
    }
  }, [content, CDNIRestart, CDNI_SystemRestart]);

  // CDNIUnexpectedErrorPopup
  useEffect(() => {
    if (content) {
      if (CDNIUnexpectedError && CDNIUnexpectedErrorMessage && CDNIUnexpectedErrorCode) {
        const isIOS = device_os === DeviceType.iOS;

        const _error: ErrorMessage = {
          heading: content.error,
          message: CDNIUnexpectedErrorMessage,
        };

        if (!isIOS) {
          _error.cta = content.exitGame;
          _error.cdniExit = CDNI_Quit();
        }

        setError(_error);
      }
    }
  }, [content, CDNIUnexpectedError, CDNIUnexpectedErrorMessage, CDNIUnexpectedErrorCode, CDNI_Quit, device_os]);

  // If Offline
  useEffect(() => {
    if (content && !isOnline) {
      setError({
        heading: content.offlineHeading,
        message: content.offlineDesc,
      });
    }

    // Only clear this error if it is indeed the offline error
    if (content && isOnline && error?.message === content.offlineDesc) {
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, isOnline]);

  // CDNIRecoverableErrorPopup
  useEffect(() => {
    if (content) {
      if (CDNIPromptName && CDNIRecoverableErrorMessage) {
        setError({
          heading: content.notice,
          message: CDNIRecoverableErrorMessage,
          cta: content.retry,
          cdniExit: CDNI_RestoreState,
        });
      }
    }
  }, [content, CDNIPromptName, CDNIRecoverableErrorMessage, CDNI_RestoreState]);

  // CDNIGoToAppStorePopup
  useEffect(() => {
    if (content) {
      if (CDNIGoToAppStore) {
        setError({
          heading: content.newUpdateAvailable,
          message: content.gotToAppStore,
          cta: content.update,
          cdniExit: CDNI_GoToAppStore,
        });
      }
    }
  }, [content, CDNIGoToAppStore, CDNI_GoToAppStore]);

  // CDNIPromptErrorMessage
  useEffect(() => {
    if (content) {
      if (CDNIPromptErrorMessage) {
        setError({
          heading: content.notice,
          message: CDNIPromptErrorMessage,
        });
      }
    }
  }, [content, CDNIPromptErrorMessage]);

  // CDNIPromptMessage
  useEffect(() => {
    if (content) {
      if (CDNIPromptMessage) {
        setError({
          heading: content.notice,
          message: CDNIPromptMessage,
        });
      }
    }
  }, [content, CDNIPromptMessage]);

  // CDNICheckDiskSpaceFailedPopup
  useEffect(() => {
    if (content) {
      const isIOS = device_os === DeviceType.iOS;
      const message = (isIOS ? content.insuffcientDiskSpaceIos : content.insuffcientDiskSpaceAndroid).replace('&&1', getMb(GetTotalSizeToDownload));

      if (CDNIShowDiskSpaceFailedPopup && diskFreeSize) {
        setError({
          heading: content.deviceStorageFull,
          message: message,
          cta: content.retry,
          cdniExit: CDNI_IgnoreCheckDiskSpaceFailure,
          goToSettingsCta: isIOS ? content.goToSettings : content.goToStorage,
          persistOnExit: true,
        });
      } else if (CDNIShowDiskSpaceFailedPopup === false && error?.message === message) {
        // Only clear this error if it is indeed the offline error
        if (content && isOnline && error?.message === content.offlineDesc) {
          setError(null);
        }
      }
    }
  }, [content, CDNIShowDiskSpaceFailedPopup, diskFreeSize, CDNI_IgnoreCheckDiskSpaceFailure, GetTotalSizeToDownload, device_os, isOnline, error]);

  // CDNICellularDownloadPopup
  useEffect(() => {
    if (content) {
      if (CDNICellularDownloadTip && !CDNICellularDownloadTipClose) {
        setError({
          heading: content.notice,
          message: content.cellularNetworkDownloadTip.replace('&&1', getMb(GetTotalSizeToDownload - GetDownloadedSizeInBytes)),
          cta: content.cellularNetworkDownload,
          cdniExit: CDNI_SetAllowCellularDownload,
        });
      }
    }
  }, [content, CDNICellularDownloadTip, GetTotalSizeToDownload, GetDownloadedSizeInBytes, CDNI_SetAllowCellularDownload, CDNICellularDownloadTipClose]);

  // CDNI_Screen_Error
  useEffect(() => {
    if (error) {
      dlog({
        phase: 'CDNI_Screen_Error',
        action: 'Render',
        targets: error.message as LoginFunnelTargets,
      });
    }
  }, [dlog, error]);

  // CDNICellularDownloadTipClose
  useEffect(() => {
    if (CDNICellularDownloadTipClose) {
      setError(null);
    }
  }, [CDNICellularDownloadTipClose]);

  // If the user has cached auth token and the CDNI is finished show the update check (client is auto logging them in)
  const isLoading = Boolean(isUsingCachedLoginToken && isCDNIFinished);

  // If we do not yet have any download speed we are checking for updates
  const isCheckingForUpdates = GetAverageDownloadSpeed === 0 && !isLoading;

  useEffect(() => {
    const startTimeMs = Date.now();
    const endTimeMs = startTimeMs + (startTimeMs - prevTick.current);
    const durationMS = endTimeMs - startTimeMs;
    const startBuffer = buffer;
    const endBuffer = GetDownloadedSizeInBytes;
    let timeout: ReturnType<typeof setInterval> | null = null;

    // If our duration is too close return - this is the first set
    if (durationMS < 100) {
      return;
    }

    const count = () => {
      timeout = setTimeout(() => {
        setBuffer(clamp(lerp(startBuffer, endBuffer, (Date.now() - startTimeMs) / durationMS), 0, GetTotalSizeToDownload));

        if (endTimeMs > Date.now()) {
          count();
        }
      }, 1000 / 10);
    };

    count();
    prevTick.current = startTimeMs;

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
    // only run effect when we get new download chunk data, buffer is updated internally, but only used for the starting value, so do not pass buffer to the deps array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GetDownloadedSizeInBytes, GetTotalSizeToDownload]);

  // Listen for Offline Status 2.9+ only
  useEffect(() => {
    if (isConnected !== null) {
      setIsOnline(isConnected);
    }
  }, [isConnected]);

  // Assign Online / Offline Listeners 2.8 and below
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    autoLoginStatus,
    buffer,
    closeWebView,
    diskFreeSize,
    error,
    fireExit,
    GetAverageDownloadSpeed,
    GetDownloadedSizeInBytes,
    getMb,
    GetTotalSizeToDownload,
    InstallingVersion,
    isCDNIFinished,
    isCheckingForUpdates,
    isLoading,
    isMobileSharedAccountEnabled,
    NowVersion,
    openDeviceStorage,
    ShowUsingDevTip,
    CDNICellularDownloadTipClose,
    CDNIUnexpectedError,
    CDNIPromptName,
    CDNIRecoverableErrorMessage,
    CDNIGoToAppStore,
  };
}
